import * as React from 'react';
import {
    Modal,
    Tabs,
    Stack,
    Group,
    Select,
    Text,
    Button,
    ActionIcon,
    ScrollArea,
    TextInput,
    NumberInput,
    MultiSelect,
    Box,
    useMantineColorScheme,
} from '@mantine/core';
import { Plus, Trash, Filter, SortAsc } from 'lucide-react';
import { ReportColumn, SortConfig, FilterRule, FilterOperator } from '@bi-studio/shared/lib';

interface AdvancedFilterSortModalProps {
    opened: boolean;
    onClose: () => void;
    columns: ReportColumn[];
    sortConfigs: SortConfig[];
    filterRules: FilterRule[];
    onApply: (sortConfigs: SortConfig[], filterRules: FilterRule[]) => void;
    columnUniqueValues: Record<string, string[]>;
}

const OPERATORS: { value: FilterOperator; label: string; types: string[] }[] = [
    { value: 'equals', label: 'Equals', types: ['string', 'numeric', 'date', 'boolean'] },
    { value: 'not_equals', label: 'Not Equals', types: ['string', 'numeric', 'date', 'boolean'] },
    { value: 'contains', label: 'Contains', types: ['string'] },
    { value: 'not_contains', label: 'Does not contain', types: ['string'] },
    { value: 'starts_with', label: 'Starts with', types: ['string'] },
    { value: 'ends_with', label: 'Ends with', types: ['string'] },
    { value: 'greater_than', label: 'Greater than', types: ['numeric', 'date'] },
    { value: 'less_than', label: 'Less than', types: ['numeric', 'date'] },
    { value: 'greater_than_or_equal', label: 'Greater than or equal', types: ['numeric', 'date'] },
    { value: 'less_than_or_equal', label: 'Less than or equal', types: ['numeric', 'date'] },
    { value: 'between', label: 'Between', types: ['numeric', 'date'] },
    { value: 'in', label: 'In list', types: ['string', 'numeric'] },
    { value: 'is_null', label: 'Is empty', types: ['string', 'numeric', 'date', 'boolean'] },
    { value: 'is_not_null', label: 'Is not empty', types: ['string', 'numeric', 'date', 'boolean'] },
];

export const AdvancedFilterSortModal: React.FC<AdvancedFilterSortModalProps> = ({
    opened,
    onClose,
    columns,
    sortConfigs: initialSortConfigs,
    filterRules: initialFilterRules,
    onApply,
    columnUniqueValues,
}) => {
    const [sortConfigs, setSortConfigs] = React.useState<SortConfig[]>(initialSortConfigs);
    const [filterRules, setFilterRules] = React.useState<FilterRule[]>(initialFilterRules);
    const [activeTab, setActiveTab] = React.useState<string | null>('sort');
    const { colorScheme } = useMantineColorScheme();

    React.useEffect(() => {
        if (opened) {
            setSortConfigs(initialSortConfigs);
            setFilterRules(initialFilterRules);
        }
    }, [opened, initialSortConfigs, initialFilterRules]);

    const addSort = () => {
        const availableColumn = columns.find(c => !sortConfigs.find(s => s.columnName === c.name));
        if (availableColumn) {
            setSortConfigs([...sortConfigs, { columnName: availableColumn.name, order: 'asc' }]);
        } else if (columns.length > 0) {
            setSortConfigs([...sortConfigs, { columnName: columns[0].name, order: 'asc' }]);
        }
    };

    const removeSort = (index: number) => {
        setSortConfigs(sortConfigs.filter((_, i) => i !== index));
    };

    const updateSort = (index: number, updates: Partial<SortConfig>) => {
        const newConfigs = [...sortConfigs];
        newConfigs[index] = { ...newConfigs[index], ...updates };
        setSortConfigs(newConfigs);
    };

    const addFilter = () => {
        if (columns.length > 0) {
            setFilterRules([...filterRules, { columnName: columns[0].name, operator: 'equals', value: '' }]);
        }
    };

    const removeFilter = (index: number) => {
        setFilterRules(filterRules.filter((_, i) => i !== index));
    };

    const updateFilter = (index: number, updates: Partial<FilterRule>) => {
        const newRules = [...filterRules];
        newRules[index] = { ...newRules[index], ...updates };
        setFilterRules(newRules);
    };

    const handleApply = () => {
        onApply(sortConfigs, filterRules);
        onClose();
    };

    const getOperatorsForColumn = (columnName: string) => {
        const col = columns.find(c => c.name === columnName);
        if (!col) return OPERATORS;
        return OPERATORS.filter(op => op.types.includes(col.dataType.toLowerCase()) || op.types.includes('string'));
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Advanced Filter & Sort"
            size="lg"
            centered
            fullScreen={false}
            transitionProps={{ transition: 'pop' }}
            overlayProps={{
                backgroundOpacity: 0.1,
                blur: 2,
            }}
        >
            <Tabs 
                value={activeTab} 
                onChange={setActiveTab}
                classNames={{
                    list: 'flex-nowrap overflow-x-auto hide-scrollbar',
                    tab: 'shrink-0'
                }}
            >
                <Tabs.List mb="md">
                    <Tabs.Tab value="sort" leftSection={<SortAsc size={16} />}>Sort</Tabs.Tab>
                    <Tabs.Tab value="filter" leftSection={<Filter size={16} />}>Filter</Tabs.Tab>
                </Tabs.List>

                <Box
                    p="md"
                    style={{
                        border: '1px solid var(--modal-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                        background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                        marginBottom: 'var(--mantine-spacing-md)'
                    }}
                >
                    <Tabs.Panel value="sort">
                        <Stack gap="sm">
                            <Text size="sm" c="dimmed">Define multiple levels of sorting for your data.</Text>
                            <ScrollArea.Autosize mah={400} type="auto">
                                <Stack gap="xs">
                                    {sortConfigs.map((config, idx) => (
                                        <Box key={idx} p="xs" style={{ border: '1px solid var(--modal-border)', borderRadius: 'var(--mantine-radius-sm)', backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)' }}>
                                            <Stack gap="xs">
                                                <Group justify="space-between">
                                                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                                                        {idx === 0 ? 'Sort by' : 'Then by'}
                                                    </Text>
                                                    <ActionIcon color="red" variant="subtle" size="sm" onClick={() => removeSort(idx)}>
                                                        <Trash size={14} />
                                                    </ActionIcon>
                                                </Group>
                                                <Group gap="xs" grow>
                                                    <Select
                                                        data={columns.map(c => ({ value: c.name, label: c.displayName || c.name }))}
                                                        value={config.columnName}
                                                        onChange={(val) => updateSort(idx, { columnName: val! })}
                                                        size="sm"
                                                        placeholder="Column"
                                                    />
                                                    <Select
                                                        data={[
                                                            { value: 'asc', label: 'Ascending' },
                                                            { value: 'desc', label: 'Descending' },
                                                        ]}
                                                        value={config.order}
                                                        onChange={(val) => updateSort(idx, { order: val as any })}
                                                        size="sm"
                                                    />
                                                </Group>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </ScrollArea.Autosize>
                            <Button
                                variant="light"
                                color="gray"
                                leftSection={<Plus size={16} />}
                                onClick={addSort}
                                fullWidth
                                disabled={sortConfigs.length >= columns.length && columns.length > 0}
                            >
                                Add Sort Level
                            </Button>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="filter">
                        <Stack gap="sm">
                            <Text size="sm" c="dimmed">Apply multiple filters to narrow down your results.</Text>
                            <ScrollArea.Autosize mah={400} type="auto" offsetScrollbars>
                                <Stack gap="xs">
                                    {filterRules.map((rule, idx) => {
                                        const column = columns.find(c => c.name === rule.columnName);
                                        const operators = getOperatorsForColumn(rule.columnName);

                                        return (
                                            <Box key={idx} p="xs" style={{ border: '1px solid var(--modal-border)', borderRadius: 'var(--mantine-radius-sm)', backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)' }}>
                                                <Stack gap="xs">
                                                    <Group gap="xs" wrap="nowrap">
                                                        <Select
                                                            data={columns.map(c => ({ value: c.name, label: c.displayName || c.name }))}
                                                            value={rule.columnName}
                                                            onChange={(val) => updateFilter(idx, { columnName: val!, operator: 'equals', value: '' })}
                                                            style={{ flex: 1 }}
                                                            size="sm"
                                                        />
                                                        <ActionIcon color="red" variant="subtle" size="sm" onClick={() => removeFilter(idx)}>
                                                            <Trash size={14} />
                                                        </ActionIcon>
                                                    </Group>
                                                    <Group gap="xs" wrap="nowrap">
                                                        <Select
                                                            data={operators.map(op => ({ value: op.value, label: op.label }))}
                                                            value={rule.operator}
                                                            onChange={(val) => updateFilter(idx, { operator: val as any })}
                                                            w={150}
                                                            size="sm"
                                                        />
                                                        {!['is_null', 'is_not_null'].includes(rule.operator) && (
                                                            <Box style={{ flex: 1 }}>
                                                                {rule.operator === 'in' ? (
                                                                    <MultiSelect
                                                                        data={columnUniqueValues[rule.columnName] || []}
                                                                        value={Array.isArray(rule.value) ? rule.value : []}
                                                                        onChange={(val) => updateFilter(idx, { value: val })}
                                                                        size="sm"
                                                                        placeholder="Select values"
                                                                        searchable
                                                                    />
                                                                ) : rule.operator === 'between' ? (
                                                                    <Group gap="xs" wrap="nowrap">
                                                                        {column?.dataType === 'numeric' ? (
                                                                            <>
                                                                                <NumberInput
                                                                                    size="sm"
                                                                                    placeholder="Min"
                                                                                    value={Array.isArray(rule.value) ? rule.value[0] : ''}
                                                                                    onChange={(val) => updateFilter(idx, { value: [val, Array.isArray(rule.value) ? rule.value[1] : ''] })}
                                                                                />
                                                                                <NumberInput
                                                                                    size="sm"
                                                                                    placeholder="Max"
                                                                                    value={Array.isArray(rule.value) ? rule.value[1] : ''}
                                                                                    onChange={(val) => updateFilter(idx, { value: [Array.isArray(rule.value) ? rule.value[0] : '', val] })}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <TextInput
                                                                                    size="sm"
                                                                                    placeholder="Min"
                                                                                    value={Array.isArray(rule.value) ? rule.value[0] : ''}
                                                                                    onChange={(e) => updateFilter(idx, { value: [e.target.value, Array.isArray(rule.value) ? rule.value[1] : ''] })}
                                                                                />
                                                                                <TextInput
                                                                                    size="sm"
                                                                                    placeholder="Max"
                                                                                    value={Array.isArray(rule.value) ? rule.value[1] : ''}
                                                                                    onChange={(e) => updateFilter(idx, { value: [Array.isArray(rule.value) ? rule.value[0] : '', e.target.value] })}
                                                                                />
                                                                            </>
                                                                        )}
                                                                    </Group>
                                                                ) : column?.dataType === 'numeric' ? (
                                                                    <NumberInput
                                                                        size="sm"
                                                                        value={rule.value}
                                                                        onChange={(val) => updateFilter(idx, { value: val })}
                                                                    />
                                                                ) : (
                                                                    <TextInput
                                                                        size="sm"
                                                                        value={rule.value}
                                                                        onChange={(e) => updateFilter(idx, { value: e.target.value })}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Group>
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </ScrollArea.Autosize>
                            <Button
                                variant="light"
                                color="gray"
                                leftSection={<Plus size={16} />}
                                onClick={addFilter}
                                fullWidth
                            >
                                Add Filter Rule
                            </Button>
                        </Stack>
                    </Tabs.Panel>
                </Box>
            </Tabs>

            <Group justify="flex-end" pt="md">
                <Button variant={colorScheme === 'dark' ? 'light' : 'subtle'} color="gray" onClick={onClose}>Cancel</Button>
                <Button onClick={handleApply} color={colorScheme === 'dark' ? 'gray.8' : 'brand'} variant="filled">Apply</Button>
            </Group>
        </Modal>
    );
};
